// Connect / finish-setup flows for one toolkit. Secrets are write-only: inputs are
// password fields, never pre-filled, cleared after every submit, and nothing the user
// typed is ever rendered back (the gateway only returns key NAMES).
import { useId, useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../../api/client'
import { errorMessage } from '../../api/errors'
import { qk } from '../../api/queries'
import type { OAuthConnectLink, Server } from '../../api/types'
import { useSession } from '../../context/SessionContext'
import { Modal } from '../../components/Modal'
import { Badge } from '../../components/ui'
import { STATE_BADGE, type ToolkitRow } from '../toolkits'

function useInvalidateConnections() {
  const qc = useQueryClient()
  const { workspaceId } = useSession()
  return () => {
    void qc.invalidateQueries({ queryKey: qk.connections(workspaceId) })
    void qc.invalidateQueries({ queryKey: qk.connectedAccounts(workspaceId) })
    void qc.invalidateQueries({ queryKey: ['tool-search', workspaceId] })
  }
}

function FormError({ error }: { error: unknown }) {
  if (!error) return null
  const missing = isApiError(error) ? error.missing_secrets : undefined
  return (
    <div className="form-error" role="alert">
      <p>{errorMessage(error)}</p>
      {missing && missing.length > 0 && (
        <p>
          Still missing: <code>{missing.join(', ')}</code>
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// OAuth (managed providers and MCP-upstream OAuth vendors)

function OAuthFlow({ row }: { row: ToolkitRow }) {
  const invalidate = useInvalidateConnections()
  const [waiting, setWaiting] = useState<string | null>(null)
  const reconnect = row.state === 'needs_reconnect'

  const start = useMutation({
    mutationFn: () => apiFetch<OAuthConnectLink>(`/oauth/${encodeURIComponent(row.providerName)}/authorize`),
    onSuccess: (r) => {
      const win = window.open(r.connect_link, 'symone-oauth', 'popup,width=600,height=720')
      setWaiting(win ? '' : r.connect_link)
    },
  })
  const disconnect = useMutation({
    mutationFn: () => apiFetch(`/connected-accounts/${encodeURIComponent(row.providerName)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })

  return (
    <div className="connect-flow">
      <p>
        {row.name} uses OAuth. You’ll sign in with {row.name} in a popup and approve access; Symone stores the
        connection for <strong>your</strong> account in this workspace. Tokens are encrypted and never shown.
      </p>
      {reconnect && (
        <p className="warn-note" role="note">
          Your {row.name} connection needs reconnecting: the provider revoked access, a token refresh failed, or the
          auth config was disabled.
        </p>
      )}
      <FormError error={start.error ?? disconnect.error} />
      {waiting !== null && (
        <div className="connect-wait" role="status">
          {waiting ? (
            <p>
              Your browser blocked the popup.{' '}
              <a href={waiting} target="_blank" rel="noopener noreferrer">Open the {row.name} sign-in page</a>, then come back.
            </p>
          ) : (
            <p>Finish signing in to {row.name} in the popup window.</p>
          )}
          <button type="button" className="btn btn--sm" onClick={invalidate}>I’ve finished, refresh status</button>
        </div>
      )}
      <div className="connect-actions">
        <button type="button" className="btn btn--primary" disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? 'Opening…' : reconnect ? `Reconnect ${row.name}` : row.account ? `Connect ${row.name} again` : `Connect ${row.name}`}
        </button>
        {row.account && (
          <button type="button" className="btn btn--ghost-danger" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
            Disconnect
          </button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Vendor MCP server with a per-user API key / bearer token

function UpstreamKeyFlow({ row }: { row: ToolkitRow }) {
  const id = useId()
  const invalidate = useInvalidateConnections()
  const [key, setKey] = useState('')
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: (api_key: string) =>
      apiFetch(`/connected-accounts/${encodeURIComponent(row.providerName)}/api-key`, {
        method: 'PUT',
        body: JSON.stringify({ api_key }),
      }),
    onSuccess: () => {
      setSaved(true)
      invalidate()
    },
    onSettled: () => setKey(''),
  })
  const disconnect = useMutation({
    mutationFn: () => apiFetch(`/connected-accounts/${encodeURIComponent(row.providerName)}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const v = key.trim()
    if (!v || save.isPending) return
    setSaved(false)
    save.mutate(v)
  }

  return (
    <form className="connect-flow" onSubmit={submit} noValidate>
      <p>
        {row.name} is the vendor’s own MCP server. It uses <strong>your</strong> API key (or bearer token): every call
        you make uses only your key. Symone checks it with {row.name} once, then stores it encrypted. It is never shown again.
      </p>
      {row.state === 'needs_reconnect' && (
        <p className="warn-note" role="note">Your stored key stopped working. Enter a new one.</p>
      )}
      <div className="field">
        <label htmlFor={`${id}-key`}>
          API key <span className="cell-sub">(required)</span>
        </label>
        <input
          id={`${id}-key`}
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={key}
          placeholder={row.account ? 'Enter a new key to replace the stored one' : ''}
          onChange={(e) => setKey(e.target.value)}
        />
      </div>
      <FormError error={save.error ?? disconnect.error} />
      {saved && <p className="ok-note" role="status">Key verified and saved.</p>}
      <div className="connect-actions">
        <button type="submit" className="btn btn--primary" disabled={!key.trim() || save.isPending}>
          {save.isPending ? 'Checking key…' : row.account ? 'Replace key' : 'Save key'}
        </button>
        {row.account && (
          <button type="button" className="btn btn--ghost-danger" disabled={disconnect.isPending} onClick={() => disconnect.mutate()}>
            Remove key
          </button>
        )}
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Workspace secrets (built-in API-key and OpenAPI connectors) and no-auth toolkits

interface ServerInfo {
  id: string
  status: Server['status']
  required: string[]
  present: string[]
}

function SecretsFlow({ row }: { row: ToolkitRow }) {
  const id = useId()
  const { workspaceId } = useSession()
  const invalidate = useInvalidateConnections()
  const [server, setServer] = useState<ServerInfo | null>(() =>
    row.connection
      ? {
          id: row.connection.server_id,
          status: row.connection.status,
          required: row.connection.required_secrets ?? [],
          present: row.connection.connected_secrets ?? [],
        }
      : null,
  )
  const [values, setValues] = useState<Record<string, string>>({})
  const [validate, setValidate] = useState(false)
  const [done, setDone] = useState(false)

  const enable = useMutation({
    mutationFn: () =>
      apiFetch<Server>('/servers', {
        method: 'POST',
        body: JSON.stringify({ server_type: row.key, display_name: row.name, workspace_id: workspaceId }),
      }),
    onSuccess: (s) => {
      setServer({ id: s.id, status: s.status, required: s.required_secrets ?? [], present: [] })
      if (s.status === 'active') setDone(true)
      invalidate()
    },
  })

  const finish = useMutation({
    mutationFn: async (srv: ServerInfo) => {
      const entries = Object.entries(values).filter(([, v]) => v.trim())
      const present = new Set(srv.present)
      for (const [key, value] of entries) {
        await apiFetch(`/servers/${srv.id}/secrets/${encodeURIComponent(key)}`, {
          method: 'PUT',
          body: JSON.stringify({ value }),
        })
        present.add(key)
      }
      let status = srv.status
      if (status !== 'active') {
        const s = await apiFetch<Server>(
          `/servers/${srv.id}/activate${validate ? '?validate_credentials=true' : ''}`,
          { method: 'POST' },
        )
        status = s.status
      }
      return { ...srv, status, present: [...present] }
    },
    onSuccess: (s) => {
      setServer(s)
      setDone(s.status === 'active')
    },
    onSettled: () => {
      setValues({})
      invalidate()
    },
  })

  if (!server) {
    const names = row.requiredSecrets
    return (
      <div className="connect-flow">
        {row.kind === 'none' ? (
          <p>{row.name} needs no credentials. Enable it to make its tools available to this workspace.</p>
        ) : (
          <>
            <p>
              {row.name} uses a workspace credential: every member, API key and tool session in this workspace uses the
              same stored secrets. Enable the toolkit first; Symone then asks for each secret it needs.
            </p>
            {names.length > 0 && (
              <p className="cell-sub">Needs: {names.map((n) => <code key={n} className="secret-name">{n}</code>)}</p>
            )}
          </>
        )}
        <FormError error={enable.error} />
        <div className="connect-actions">
          <button type="button" className="btn btn--primary" disabled={enable.isPending} onClick={() => enable.mutate()}>
            {enable.isPending ? 'Enabling…' : `Enable ${row.name}`}
          </button>
        </div>
      </div>
    )
  }

  const missing = server.required.filter((k) => !server.present.includes(k) && !values[k]?.trim())
  const anyValue = Object.values(values).some((v) => v.trim())
  const canSubmit = !finish.isPending && missing.length === 0 && (anyValue || server.status !== 'active')

  if (server.required.length === 0) {
    return (
      <div className="connect-flow">
        {server.status === 'active' || done ? (
          <p className="ok-note" role="status">{row.name} is enabled and ready.</p>
        ) : (
          <>
            <p>{row.name} is {STATE_BADGE[row.state].label.toLowerCase()}. Activate it to make its tools available.</p>
            <FormError error={finish.error} />
            <div className="connect-actions">
              <button type="button" className="btn btn--primary" disabled={finish.isPending} onClick={() => finish.mutate(server)}>
                {finish.isPending ? 'Activating…' : 'Activate'}
              </button>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <form
      className="connect-flow"
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        if (canSubmit) finish.mutate(server)
      }}
    >
      <p>
        Enter {server.required.length === 1 ? 'the secret' : `all ${server.required.length} secrets`} {row.name} needs. Values are
        encrypted at rest, shared by everyone in this workspace and never shown again.
      </p>
      {server.status === 'pending_config' && !done && (
        <p className="warn-note" role="note">
          Setup isn’t finished: the toolkit stays inactive until every secret is set and it is activated.
        </p>
      )}
      {server.required.map((k) => {
        const isSet = server.present.includes(k)
        return (
          <div className="field" key={k}>
            <label htmlFor={`${id}-${k}`}>
              <code>{k}</code> {isSet ? <Badge tone="success">Set</Badge> : <span className="cell-sub">(required)</span>}
            </label>
            <input
              id={`${id}-${k}`}
              type="password"
              autoComplete="off"
              spellCheck={false}
              value={values[k] ?? ''}
              placeholder={isSet ? 'Leave blank to keep the stored value' : ''}
              onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
            />
          </div>
        )
      })}
      {server.status !== 'active' && (
        <label className="check">
          <input type="checkbox" checked={validate} onChange={(e) => setValidate(e.target.checked)} />
          Check the credentials with {row.name} before activating
        </label>
      )}
      <FormError error={finish.error} />
      {done && <p className="ok-note" role="status">{row.name} is connected and active.</p>}
      <div className="connect-actions">
        <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
          {finish.isPending ? 'Saving…' : server.status === 'active' ? 'Update secrets' : 'Save and activate'}
        </button>
        {missing.length > 0 && <span className="cell-sub">{missing.length} required secret{missing.length === 1 ? '' : 's'} still empty</span>}
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------

export function ConnectModal({ row, onClose }: { row: ToolkitRow; onClose: () => void }) {
  const badge = STATE_BADGE[row.state]
  return (
    <Modal
      title={`Connect ${row.name}`}
      onClose={onClose}
      footer={<button type="button" className="btn" onClick={onClose}>Close</button>}
    >
      <p className="connect-status">
        Status: <Badge tone={badge.tone}>{badge.label}</Badge>
      </p>
      {row.kind === 'oauth' && <OAuthFlow row={row} />}
      {row.kind === 'upstream_key' && <UpstreamKeyFlow row={row} />}
      {(row.kind === 'secrets' || row.kind === 'none') && <SecretsFlow row={row} />}
    </Modal>
  )
}
